// Branded identifiers (app-engineering §2: make illegal states unrepresentable).
//
// Every entity id is a distinct branded string derived from a Zod UUID schema, so a
// UserId cannot be passed where an AppId is expected and both are rejected at the
// boundary unless they are real UUIDs. The schema IS the validating constructor:
// `AccountId.parse(x)` / `AccountId.safeParse(x)` produce the branded value; nothing
// downstream re-checks (parse, don't validate). Each brand is written inline
// (`z.uuid().brand<"X">()`) rather than via a helper - the brand type is per-id.
import { z } from "zod";
// --- Identity and tenancy ---
export const AccountId = z.uuid().brand();
export const UserId = z.uuid().brand();
export const MembershipId = z.uuid().brand();
// --- Apps and connections ---
export const AppId = z.uuid().brand();
export const ConnectionId = z.uuid().brand();
export const EnvironmentId = z.uuid().brand();
/** A repo fingerprint row's id (data-model.md fingerprints; Slice 2). */
export const FingerprintId = z.uuid().brand();
// --- Vault ---
/**
 * The opaque handle the vault returns from `store` and accepts in `lease`. A UUID by
 * construction; a plaintext secret value is never a SecretRef.
 */
export const SecretRef = z.uuid().brand();
export const SecretServiceMapId = z.uuid().brand();
// --- The shift: fixes, runs, ledger ---
export const FixId = z.uuid().brand();
export const RunId = z.uuid().brand();
export const RunnerId = z.uuid().brand();
export const RunLedgerId = z.uuid().brand();
export const AuditLogId = z.uuid().brand();
// --- The shift: intake, jobs, cards, shifts, incidents (Slice 1) ---
export const IntakeItemId = z.uuid().brand();
export const JobId = z.uuid().brand();
export const ChangeCardId = z.uuid().brand();
export const ShiftId = z.uuid().brand();
export const IncidentId = z.uuid().brand();
// --- Instrumentation and ingest (Slice 1) ---
export const EventId = z.uuid().brand();
export const FeedbackSubmissionId = z.uuid().brand();
/** A per-app public ingest key row's id (data-model.md app_keys). The key VALUE is not a UUID; this is the row id. */
export const AppKeyId = z.uuid().brand();
// --- Knowledge layer (data-model.md:45-47; D40; Slice 3) ---
/** A knowledge_nodes row id (data-model.md:45). */
export const KnowledgeNodeId = z.uuid().brand();
/** A knowledge_edges row id (data-model.md:46). */
export const KnowledgeEdgeId = z.uuid().brand();
/** A knowledge_chunks row id (data-model.md:47). */
export const KnowledgeChunkId = z.uuid().brand();
// --- Money, plans, usage ---
export const PlanId = z.uuid().brand();
export const SubscriptionId = z.uuid().brand();
export const UsageCounterId = z.uuid().brand();
// --- Money view (Slice 6; slice-plan.md:83-92; ADR-0023 - the D27 billing inbox) ---
/** A billing_inbox_items row id (ADR-0023 decision 6: a NEW table, not in data-model.md). */
export const BillingInboxItemId = z.uuid().brand();
// --- Pulse (Slice 6; data-model.md:44 metrics_snapshots) ---
export const MetricSnapshotId = z.uuid().brand();
// --- Feedback overview (Slice 6; data-model.md:41-42 feedback_themes/feature_requests; D45) ---
export const FeedbackThemeId = z.uuid().brand();
export const FeatureRequestId = z.uuid().brand();
// --- Value-shaped brands (not entity ids) ---
/**
 * A model identifier (for example a provider's model name). Bounded, non-empty. Kept
 * distinct from a free string so it cannot be confused with prompt content or a key.
 */
export const ModelId = z.string().trim().min(1).max(200).brand();
/**
 * A lowercase hex SHA-256 digest of a prompt. cacheKey accepts ONLY this, never raw
 * prompt text, so tenant content structurally cannot enter a cache key. Producers
 * (the gateway) compute the digest upstream; this package only validates its shape.
 */
export const PromptHash = z
    .string()
    .regex(/^[0-9a-f]{64}$/, "must be a lowercase hex SHA-256 digest")
    .brand();
//# sourceMappingURL=ids.js.map